import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import AdminLayout from '../../layouts/AdminLayout';
import KanbanBoard from '../../components/KanbanBoard';
import { ordersApi } from '../../services/api';
import { formatApiError } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getKanbanColumns } from '../../config/businessTypes';

export default function KanbanPage() {
  const { lang, t } = useLanguage();
  const { businessType } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visiblePerStatus, setVisiblePerStatus] = React.useState(5);

  const { data, isLoading } = useQuery({
    queryKey: ['orders-kanban'],
    queryFn: () => ordersApi.list({ limit: 100 }).then(r => r.data.items || []),
    refetchInterval: 15000,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => ordersApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders-kanban'] });
      toast.success(t('Status pesanan diperbarui', 'Order status updated'));
    },
    onError: err => toast.error(formatApiError(err)),
  });

  const handleStatusChange = (orderId, newStatus) => {
    statusMutation.mutate({ id: orderId, status: newStatus });
  };
  const hasMoreOrders = getKanbanColumns(businessType).some(column => (data || []).filter(order => order.status === column.id).length > visiblePerStatus);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">Kanban Board</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Drag & drop untuk ubah status pesanan', 'Drag & drop to change order status')}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => queryClient.invalidateQueries({ queryKey: ['orders-kanban'] })}
              className="p-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
              <RefreshCw size={18} />
            </button>
            <button onClick={() => navigate('/admin/order-create')} data-testid="create-order-button"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
              <Plus size={18} />{t('Buat Pesanan', 'New Order')}
            </button>
          </div>
        </div>

        <KanbanBoard orders={data || []} onStatusChange={handleStatusChange} isLoading={isLoading} adminPath="/admin" cardLimit={visiblePerStatus} />
        {hasMoreOrders && (
          <div className="flex justify-center">
            <button onClick={() => setVisiblePerStatus(count => count + 5)} data-testid="kanban-load-more-button"
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors">
              <ChevronDown size={16} />{t('Muat 5 pesanan lagi per status', 'Load 5 more orders per status')}
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
